// Helper methods used by the paginated map popup (see components/Map/PaginatedPopup.js).
// When a user clicks a feature on the "anchor" layer, additional popup pages need to show
// data from other layers on the map at that same location. These methods resolve which
// feature of a sibling layer sits underneath the clicked point.

// Ray casting algorithm to determine whether a point falls within a single GeoJSON linear
// ring. Ring coordinates are GeoJSON ordered pairs, [longitude, latitude].
const pointInRing = (lng, lat, ring) => {
    let inside = false;

    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const [lngI, latI] = ring[i];
        const [lngJ, latJ] = ring[j];

        // Does a horizontal ray extending from the point cross this edge of the ring?
        // Each crossing flips us between outside and inside the ring.
        const crossesEdge =
            latI > lat !== latJ > lat &&
            lng < ((lngJ - lngI) * (lat - latI)) / (latJ - latI) + lngI;

        if (crossesEdge) {
            inside = !inside;
        }
    }

    return inside;
};

// A GeoJSON polygon is an array of linear rings. The first ring is the outer boundary and
// any remaining rings are holes cut out of it, so a point is only within the polygon if it
// falls inside the outer ring and outside of every hole.
const pointInPolygon = (lng, lat, rings) => {
    if (!rings?.length || !pointInRing(lng, lat, rings[0])) {
        return false;
    }

    return !rings.slice(1).some((holeRing) => pointInRing(lng, lat, holeRing));
};

// Determine whether a Leaflet LatLng falls within a GeoJSON Polygon or MultiPolygon.
// Returns false for any other geometry type (callers fall back to a bounds check).
export const pointInGeometry = (latlng, geometry) => {
    if (!latlng || !geometry?.coordinates) {
        return false;
    }

    // Leaflet orders coordinates as (lat, lng), GeoJSON as (lng, lat):
    const { lat, lng } = latlng;

    if (geometry.type === 'Polygon') {
        return pointInPolygon(lng, lat, geometry.coordinates);
    }

    if (geometry.type === 'MultiPolygon') {
        return geometry.coordinates.some((rings) =>
            pointInPolygon(lng, lat, rings)
        );
    }

    return false;
};

// Find a configured map layer on the map by its id.
//
// Note that more than one layer on the map answers to the same id: esri-leaflet copies a
// feature layer's options onto each individual feature it draws, and adds those features to
// the map as layers in their own right. Only the feature layer itself holds a collection of
// features, which is what callers here need to read, so the id alone is not enough to
// identify it.
export const findLayerOnMap = (map, layerId) => {
    let found = null;

    map.eachLayer((mapLayer) => {
        // eslint-disable-next-line no-underscore-dangle
        if (mapLayer.options?.id === layerId && mapLayer._layers) {
            found = mapLayer;
        }
    });

    return found;
};

// Find the feature of an esri layer that sits underneath the given point and return its
// properties (or null when the point falls outside of every feature in the layer).
// The layer's bounding boxes are used as a cheap first pass, then containment is confirmed
// against the actual geometry -- bounding boxes of irregular polygons overlap heavily, so
// a bounds check alone regularly matches the wrong feature.
export const findFeatureAtLatLng = (esriLayer, latlng) => {
    // eslint-disable-next-line no-underscore-dangle
    if (!esriLayer?._layers || !latlng) {
        return null;
    }

    const candidates = Object.values(
        // eslint-disable-next-line no-underscore-dangle
        esriLayer._layers
    ).filter((featureLayer) => featureLayer.getBounds?.()?.contains(latlng));

    if (!candidates.length) {
        return null;
    }

    const match = candidates.find((featureLayer) =>
        pointInGeometry(latlng, featureLayer.feature?.geometry)
    );

    if (match) {
        return match.feature?.properties ?? null;
    }

    // Points and lines have no interior to test against, so for those geometry types the
    // bounds match is the best (and only) answer available:
    const isPolygonCandidate = candidates.some((featureLayer) =>
        ['Polygon', 'MultiPolygon'].includes(
            featureLayer.feature?.geometry?.type
        )
    );

    return isPolygonCandidate
        ? null
        : candidates[0].feature?.properties ?? null;
};
